import { ProfanityEngine } from "profanity-guard";

export const PROFANITY_ERROR_CODE = "PROFANITY_DETECTED";
export const DEFAULT_PROFANITY_LANGUAGE = "en";

const DEFAULT_WHITELIST = ["kewordal", "kail"];
const engineCache = new Map();

const toText = (value) => {
  if (value == null) return "";
  return String(value).trim();
};

const getEngine = (language = DEFAULT_PROFANITY_LANGUAGE) => {
  const normalizedLanguage = language || DEFAULT_PROFANITY_LANGUAGE;
  if (!engineCache.has(normalizedLanguage)) {
    engineCache.set(
      normalizedLanguage,
      new ProfanityEngine({
        language: normalizedLanguage,
        whitelist: DEFAULT_WHITELIST,
      }),
    );
  }
  return engineCache.get(normalizedLanguage);
};

const formatFieldList = (fieldLabels) => {
  const labels = Array.from(
    new Set(fieldLabels.map(toText).filter(Boolean)),
  );

  if (!labels.length) return "this field";
  if (labels.length === 1) return labels[0];
  if (labels.length === 2) return `${labels[0]} and ${labels[1]}`;
  return `${labels.slice(0, -1).join(", ")}, and ${labels[labels.length - 1]}`;
};

const normalizeFieldEntries = (fields) => {
  if (!fields) return [];

  if (!Array.isArray(fields) && typeof fields === "object") {
    return Object.entries(fields).map(([field, value]) => ({
      field,
      label: field,
      value,
    }));
  }

  return (Array.isArray(fields) ? fields : [])
    .filter(Boolean)
    .flatMap((entry) => {
      if (Array.isArray(entry.value)) {
        return entry.value.map((value, index) => ({
          ...entry,
          label:
            entry.itemLabel ||
            `${entry.label || entry.field || "Field"} ${index + 1}`,
          value,
        }));
      }

      return entry;
    });
};

export class ProfanityValidationError extends Error {
  constructor({
    field,
    fieldLabel,
    message = "Please remove restricted language before submitting.",
  } = {}) {
    super(message);
    this.name = "ProfanityValidationError";
    this.code = PROFANITY_ERROR_CODE;
    this.field = field;
    this.fieldLabel = fieldLabel;
  }
}

export const containsProfanity = (
  value,
  { language = DEFAULT_PROFANITY_LANGUAGE } = {},
) => {
  const text = toText(value);
  return text ? getEngine(language).check(text) : false;
};

export const censorProfanity = (
  value,
  { language = DEFAULT_PROFANITY_LANGUAGE, replaceChar = "*" } = {},
) => getEngine(language).censor(String(value || ""), replaceChar);

export const assertNoProfanity = (
  value,
  fieldLabel = "This field",
  options = {},
) => {
  if (containsProfanity(value, options)) {
    throw new ProfanityValidationError({
      field: options.field,
      fieldLabel,
      message:
        options.message ||
        `${fieldLabel} contains restricted language. Please revise it before submitting.`,
    });
  }

  return true;
};

export const getProfanityViolations = (fields, options = {}) =>
  normalizeFieldEntries(fields)
    .map((entry) => ({
      field: entry.field,
      fieldLabel: entry.label || entry.field || "This field",
      value: entry.value,
    }))
    .filter((entry) => containsProfanity(entry.value, options));

export const assertNoProfanityInFields = (fields, options = {}) => {
  const violations = getProfanityViolations(fields, options);

  if (violations.length) {
    const fieldList = formatFieldList(
      violations.map((violation) => violation.fieldLabel),
    );

    throw new ProfanityValidationError({
      field: violations[0].field,
      fieldLabel: violations[0].fieldLabel,
      message:
        options.message ||
        `Please remove restricted language from ${fieldList} before submitting.`,
    });
  }

  return true;
};

export const isProfanityValidationError = (error) =>
  error?.code === PROFANITY_ERROR_CODE ||
  error?.name === "ProfanityValidationError";
